import { RootState } from './../redux/store';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { API_BASE_URL } from '../helpers/common';

export const settingsApi = createApi({
  reducerPath: 'settingsApi',
  tagTypes: ['CompanyIntegration'],
  baseQuery: fetchBaseQuery({
    baseUrl: API_BASE_URL,
    prepareHeaders: async (headers, { getState }) => {
      const token = (getState() as RootState).user.info.accessToken;
      token && headers.set('Authorization', `Bearer ${token}`);
      return headers;
    },
  }),
  endpoints: (build) => ({
    getUserSettings: build.query<any, string>({
      transformResponse: (response: { data: any }) => response.data,
      query: (key) => ({
        url: `/settings/key/${key}`,
        method: 'GET',
      }),
    }),
    updateUserSettings: build.mutation<any, { key: string; formData: any }>({
      transformResponse: (response: { data: any }) => response.data,
      query: ({ key, formData }) => ({
        url: `/settings/key/${key}`,
        method: 'PUT',
        body: formData,
      }),
    }),
    getCompanyIntegrationList: build.query<any, void>({
      transformResponse: (response: { data: any }) => response.data,
      query: () => ({
        url: `/settings/integration-list`,
        method: 'GET',
      }),
    }),
    getCompanyIntegrationById: build.query<any, number>({
      transformResponse: (response: { data: any }) => response.data,
      query: (id) => ({
        url: `/integrations/${id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [
        { type: 'CompanyIntegration', id },
      ],
    }),
    updateCompanyIntegration: build.mutation<any, { id: number; formData: any }>({
      transformResponse: (response: { data: any }) => response.data,
      query: ({ id, formData }) => ({
        url: `/integrations/${id}`,
        method: 'PATCH',
        body: formData,
      }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            settingsApi.util.updateQueryData(
              'getCompanyIntegrationById',
              id,
              () => data,
            ),
          );
        } catch {
          // Keep the existing cache when PATCH fails.
        }
      },
    }),
    updateUserSettingsMutation: build.mutation<any, any>({
      transformResponse: (response: { data: any }) => response.data,
      query: (data) => ({
        url: `/settings`,
        method: 'PATCH',
        body: data,
      }),
    }),
    updateCompanySettings: build.mutation<any, FormData | {
      name?: string;
      registrationNumber?: string;
      country?: string;
      relatedIndustry?: string;
      logisticSpacesCount?: number;
      mainColor?: string;
      secondaryColor?: string;
      imageUrl?: string;
    }>({
      transformResponse: (response: { data: any }) => response.data,
      query: (data) => ({
        url: `/companies/settings`,
        method: 'PATCH',
        body: data,
      }),
    }),
    checkIntegrationHealth: build.mutation<any, { id: number; apiUrl: string; apiKey: string; integration: string }>({
      transformResponse: (response: { data: any }) => response.data,
      query: ({ id, apiUrl, apiKey, integration }) => ({
        url: `/integrations/${id}/health`,
        method: 'POST',
        body: { apiUrl, apiKey, integration },
      }),
    }),
    checkQueryHealth: build.mutation<any, { 
      id: number; 
      queryIndex: number; 
      apiUrl: string; 
      apiKey: string;
      query: string;
      responseField: string;
      entity: string;
      params: any[];
      isPagination: boolean;
    }>({
      transformResponse: (response: { data: any }) => response.data,
      query: ({ id, queryIndex, apiUrl, apiKey, query, responseField, entity, params, isPagination }) => ({
        url: `/integrations/${id}/health/query`,
        method: 'POST',
        body: { queryIndex, apiUrl, apiKey, query, responseField, entity, params, isPagination },
      }),
    }),

  }),
});

export const { 
  useUpdateUserSettingsMutation, 
  useGetUserSettingsQuery, 
  useGetCompanyIntegrationListQuery, 
  useLazyGetCompanyIntegrationListQuery,
  useUpdateCompanySettingsMutation,
  useUpdateCompanyIntegrationMutation,
  useCheckIntegrationHealthMutation,
  useCheckQueryHealthMutation,
} = settingsApi;
